"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SignInBadge } from "@/components/ui/custom/sign-in-badge";

import { useChainBalances } from "@/components/layout/nav/wallet/balances/useChainBalances";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useFiatPricingContext, usePassportProvider } from "@/context";
import { useGetWalletGems } from "@/hooks/api";

import { Badge as BiomBadge } from "@biom3/react";
import type { UserProfile } from "@imtbl/passport";
import { Copy, Gem, LogOut, Minus, Settings, Wallet } from "lucide-react";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";

interface RightNavHeaderProps {
  className?: string;
  onToggleCollapse?: () => void;
  signInCallback: () => void;
}

function RightNavHeaderInner({
  className,
  onToggleCollapse,
  signInCallback,
}: RightNavHeaderProps) {
  const { isLoggedIn, userInfo, walletAddress, logout } = usePassportProvider();
  const { getTotalFiatBalance } = useFiatPricingContext();
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const router = useRouter();

  const isValidUsername = (username: string, wallet?: string): boolean => {
    const trimmed = username.trim();
    if (trimmed.length === 0) return false;
    // assume username is not valid if it matches the wallet address
    return !(wallet && trimmed.toLowerCase() === wallet.toLowerCase());
  };

  const getDisplayName = (userInfo: UserProfile | undefined) => {
    if (userInfo?.nickname) return userInfo.nickname;
    if (userInfo?.username && isValidUsername(userInfo.username, walletAddress))
      return userInfo.username;
    if (userInfo?.email) {
      const email = userInfo.email;
      if (email.length <= 28) return email;

      const [localPart, domain] = email.split("@");
      if (domain && localPart.length > 15) {
        return `${localPart.slice(0, 12)}...@${domain}`;
      }
      return `${email.slice(0, 25)}...`;
    }
    return "User";
  };

  const { walletGems, isLoading: isGemsLoading } = useGetWalletGems(
    walletAddress || null,
    false,
  );

  const { data: coinBalancesData, isLoading: isBalancesLoading } =
    useChainBalances("imtbl-zkevm", walletAddress);

  const totalUSDBalance = useMemo(() => {
    return getTotalFiatBalance(coinBalancesData ?? []);
  }, [coinBalancesData, getTotalFiatBalance]);

  const handleCopyWalletAddress = async () => {
    if (walletAddress) {
      try {
        await navigator.clipboard.writeText(walletAddress);
        setShowCopiedTooltip(true);
        setTimeout(() => setShowCopiedTooltip(false), 2000);
      } catch (err) {
        console.error("Failed to copy wallet address:", err);
      }
    }
  };

  const handleSettingsClick = () => {
    router.push("/settings");
  };

  const handleBalanceClick = () => {
    router.push("/balance");
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-end pt-3 pr-3">
        {onToggleCollapse && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            title={isLoggedIn ? "Collapse profile" : "Collapse"}
            className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10 hover:border hover:border-white transition-all duration-300 ease-in-out"
          >
            <Minus className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Loading skeleton - shown when auth state is unknown */}
      {isLoggedIn === undefined && (
        <div className="p-4 space-y-4">
          <div className="flex items-end justify-center relative">
            <Skeleton className="w-24 h-24 rounded-full bg-white/10" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-5 w-32 mx-auto bg-white/10" />
            <Skeleton className="h-4 w-40 mx-auto bg-white/10" />
          </div>
        </div>
      )}

      {isLoggedIn === false && (
        <div className="p-4 space-y-6">
          <div className="text-center cursor-pointer">
            <SignInBadge onClick={signInCallback} size="lg" />
          </div>
          <p className="text-sm text-center text-white/60 px-4">
            Earn valuable rewards while discovering fantastic new games.
          </p>
        </div>
      )}

      {isLoggedIn === true && (
        <div className="p-4 space-y-4 -mt-4">
          <div className="flex items-end justify-center relative">
            <Badge
              variant="secondary"
              className="absolute bottom-0 left-2 sm:-left-2 bg-white/10 backdrop-blur-md border border-white/80 text-white px-3 py-1 rounded-2xl text-xs font-medium w-[120px] flex justify-center items-center gap-2 hover:bg-white/20 transition-colors"
            >
              {isGemsLoading ? (
                <>
                  <Gem className="h-4 w-4" />
                  <Skeleton className="h-4 w-8 bg-white/20" />
                </>
              ) : (
                <>
                  <Gem className="h-4 w-4" />
                  {<span>{walletGems?.gems?.toLocaleString() || "0"}</span>}
                </>
              )}
            </Badge>

            <div className="relative">
              <Avatar
                className="w-24 h-24 border border-white/30 shadow-lg cursor-pointer hover:opacity-80 transition-opacity"
                onClick={onToggleCollapse}
              >
                <AvatarImage
                  src="/images/profile/spaceman.png"
                  alt={userInfo?.nickname || "User"}
                  className="object-cover"
                />
                <AvatarFallback className="bg-white/20 backdrop-blur-md text-white font-semibold border border-white/30">
                  {(userInfo?.nickname || userInfo?.email || "U")
                    .slice(0, 2)
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>

            <Badge
              variant="secondary"
              className="absolute bottom-0 right-2 sm:-right-2 bg-white/10 backdrop-blur-md border border-white/80 text-white px-3 py-1 rounded-2xl text-xs font-medium w-[120px] flex justify-center items-center gap-2 cursor-pointer hover:bg-white/20 transition-colors"
              onClick={handleBalanceClick}
            >
              {isBalancesLoading || totalUSDBalance?.amount === undefined ? (
                <>
                  <Wallet className="h-4 w-4" />
                  <Skeleton className="h-4 w-12 bg-white/20" />
                </>
              ) : (
                <>
                  <Wallet className="h-4 w-4" />
                  <span>${totalUSDBalance.amount.toString()}</span>
                </>
              )}
            </Badge>
          </div>

          <div className="text-center space-y-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <h3 className="text-white font-semibold text-lg cursor-pointer flex items-center justify-center gap-2">
                    {userInfo?.username &&
                      isValidUsername(userInfo.username, walletAddress) && (
                        <BiomBadge badgeContent={"NEW"} variant="emphasis" />
                      )}
                    <span>{getDisplayName(userInfo)}</span>
                  </h3>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{userInfo?.email || userInfo?.nickname || "User"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {walletAddress && (
              <div className="group flex items-center justify-center transition-all duration-300 ease-in-out">
                <TooltipProvider>
                  <Tooltip open={showCopiedTooltip}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={handleCopyWalletAddress}
                        className="flex items-center gap-2 px-3 py-2 rounded-3xl border border-transparent group-hover:bg-white/10 group-hover:border-white cursor-pointer transition-all duration-300 ease-in-out bg-transparent outline-none focus:outline-none transform group-hover:-translate-x-0.2"
                      >
                        <Wallet className="h-4 w-4 text-white" />
                        <span className="font-mono text-xs text-white">
                          {`${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`.toLowerCase()}
                        </span>
                        <Copy className="h-3 w-3 text-white/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {showCopiedTooltip
                          ? "Wallet address copied!"
                          : "Copy wallet address"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <div className="flex group-hover:gap-2 gap-2 transition-all duration-300 ease-in-out transform group-hover:translate-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Settings"
                    onClick={handleSettingsClick}
                    className="h-8 w-8 text-white/60 group-hover:text-white group-hover:bg-white/10 group-hover:border group-hover:border-white transition-all duration-300 ease-in-out"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={logout}
                    title="Log out"
                    className="h-8 w-8 text-white/60 group-hover:text-white group-hover:bg-white/10 group-hover:border group-hover:border-white transition-all duration-300 ease-in-out"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function RightNavHeader({
  className,
  onToggleCollapse,
  signInCallback,
}: RightNavHeaderProps) {
  return (
    <RightNavHeaderInner
      className={className}
      onToggleCollapse={onToggleCollapse}
      signInCallback={signInCallback}
    />
  );
}
